import type { TFunction } from 'i18next';
import { get } from 'lodash';

import { MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { MaasSubmitError } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { MaasGroupSpec } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { resolveModelInstancePdSeparation } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import type { ModelInstanceFormValues, ModelInstanceSpec } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { GATEWAY_NODES_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  normalizeServiceMountGroupNames,
  resolveServiceMountGroupNamesFromServices,
} from '@/delivery-type-plugin/capabilities/service/serviceMountNode';
import { isMiGatewayModuleEnabled } from '@/delivery-type-plugin/plugins/ModelInstance/gatewayModule';

export interface ServiceMountNodeOption {
  label: string;
  value: string;
}

/** PD 入口节点 value / groupNames 限定前缀（与组 name 拼接，避免 Prefill/Decode 同名冲突） */
export const MI_SERVICE_MOUNT_PD_PREFILL_PREFIX = 'prefill/';
export const MI_SERVICE_MOUNT_PD_DECODE_PREFIX = 'decode/';

export type MiPdServiceMountSegment = 'prefill' | 'decode';

export function encodeMiPdServiceMountOptionValue(segment: MiPdServiceMountSegment, groupName: string): string {
  const name = String(groupName ?? '').trim();
  return `${segment}/${name}`;
}

export function decodeMiServiceMountOptionValue(value: string): { pdSegment?: MiPdServiceMountSegment; groupName: string } {
  const raw = String(value ?? '').trim();
  if (raw.startsWith(MI_SERVICE_MOUNT_PD_PREFILL_PREFIX)) {
    return { pdSegment: 'prefill', groupName: raw.slice(MI_SERVICE_MOUNT_PD_PREFILL_PREFIX.length).trim() };
  }
  if (raw.startsWith(MI_SERVICE_MOUNT_PD_DECODE_PREFIX)) {
    return { pdSegment: 'decode', groupName: raw.slice(MI_SERVICE_MOUNT_PD_DECODE_PREFIX.length).trim() };
  }
  return { groupName: raw };
}

function collectNodesFromSegment(
  t: TFunction,
  prefixKey: string,
  runMode: MaasRunMode | string | undefined,
  masterNodes: unknown,
  workerNodes: unknown,
  pdSegment?: MiPdServiceMountSegment,
): ServiceMountNodeOption[] {
  const masters = parseNodesField(masterNodes);
  const workers = parseNodesField(workerNodes);
  const prefix = t(prefixKey);
  const isPrimarySecondary = runMode === MaasRunMode.PrimarySecondary;
  const nodes = isPrimarySecondary ? [...masters, ...workers] : workers;
  return nodes
    .map((node) => String(node?.name ?? '').trim())
    .filter(Boolean)
    .map((name) => ({
      label: `${prefix}: ${name}`,
      value: pdSegment ? encodeMiPdServiceMountOptionValue(pdSegment, name) : name,
    }));
}

/** 模型实例服务入口：网关 + 推理节点（含 PD prefill/decode）；PD 下 value 为 prefill|decode/<groupName> */
export function buildMiServiceMountNodeOptions(values: ModelInstanceFormValues, t: TFunction): ServiceMountNodeOption[] {
  const options: ServiceMountNodeOption[] = [];
  const gateway = isMiGatewayModuleEnabled(values)
    ? parseNodesField(get(values, GATEWAY_NODES_FIELD) ?? values.gatewayNodes)[0]
    : undefined;
  const gatewayName = String(gateway?.name ?? '').trim();
  if (gatewayName) {
    options.push({
      label: `${t('data-center:maas.service.entry.node.prefix.gateway')}: ${gatewayName}`,
      value: gatewayName,
    });
  }

  if (resolveModelInstancePdSeparation(values)) {
    options.push(
      ...collectNodesFromSegment(
        t,
        'data-center:maas.service.entry.node.prefix.prefill',
        values.prefillRunMode,
        values.prefillMasterNodes,
        values.prefillWorkerNodes,
        'prefill',
      ),
      ...collectNodesFromSegment(
        t,
        'data-center:maas.service.entry.node.prefix.decode',
        values.decodeRunMode,
        values.decodeMasterNodes,
        values.decodeWorkerNodes,
        'decode',
      ),
    );
  } else {
    options.push(
      ...collectNodesFromSegment(
        t,
        'data-center:maas.service.entry.node.prefix.inference',
        values.runMode,
        values.masterNodes,
        values.workerNodes,
      ),
    );
  }

  return options;
}

function inferTopologyRunMode(topology?: { headGroupSpec?: MaasGroupSpec; workerGroupSpecs?: MaasGroupSpec[] }): MaasRunMode {
  if (topology?.headGroupSpec && (topology.workerGroupSpecs?.length ?? 0) > 0) {
    return MaasRunMode.PrimarySecondary;
  }
  return MaasRunMode.Normal;
}

/** 详情/只读：从 spec 构建与创建页一致的入口节点选项 */
export function buildMiServiceMountNodeOptionsFromSpec(spec: ModelInstanceSpec | undefined, t: TFunction): ServiceMountNodeOption[] {
  if (!spec) return [];
  const options: ServiceMountNodeOption[] = [];
  const gatewayName = String(spec.gateway?.name ?? '').trim();
  if (gatewayName) {
    options.push({
      label: `${t('data-center:maas.service.entry.node.prefix.gateway')}: ${gatewayName}`,
      value: gatewayName,
    });
  }

  const inference = spec.inference;
  const pd = inference?.prefillDecode;
  if (pd?.prefill || pd?.decode) {
    if (pd.prefill) {
      options.push(
        ...collectNodesFromSegment(
          t,
          'data-center:maas.service.entry.node.prefix.prefill',
          inferTopologyRunMode(pd.prefill),
          pd.prefill.headGroupSpec ? [pd.prefill.headGroupSpec] : [],
          pd.prefill.workerGroupSpecs || [],
          'prefill',
        ),
      );
    }
    if (pd.decode) {
      options.push(
        ...collectNodesFromSegment(
          t,
          'data-center:maas.service.entry.node.prefix.decode',
          inferTopologyRunMode(pd.decode),
          pd.decode.headGroupSpec ? [pd.decode.headGroupSpec] : [],
          pd.decode.workerGroupSpecs || [],
          'decode',
        ),
      );
    }
    return options;
  }

  const topology = inference?.normal ?? inference?.primarySecondary;
  if (topology) {
    options.push(
      ...collectNodesFromSegment(
        t,
        'data-center:maas.service.entry.node.prefix.inference',
        inferTopologyRunMode(topology),
        topology.headGroupSpec ? [topology.headGroupSpec] : [],
        topology.workerGroupSpecs || [],
      ),
    );
  }
  return options;
}

/** 表单入口节点 → API groupNames（PD 限定值原样提交） */
export function resolveMiServiceMountGroupNamesForApi(values: ModelInstanceFormValues): string[] {
  return normalizeServiceMountGroupNames(values._serviceMountGroupName);
}

/** 存在网关节点时默认选中为入口（仅当尚未选择） */
export function resolveDefaultMiServiceMountGroupNames(values: ModelInstanceFormValues): string[] | undefined {
  if (!isMiGatewayModuleEnabled(values)) return undefined;
  const gateway = parseNodesField(get(values, GATEWAY_NODES_FIELD) ?? values.gatewayNodes)[0];
  const gatewayName = String(gateway?.name ?? '').trim();
  if (!gatewayName) return undefined;
  const current = values._serviceMountGroupName;
  if (current == null || current === '' || (Array.isArray(current) && !current.length)) {
    return [gatewayName];
  }
  return undefined;
}

/** 当前选中的入口节点是否仍存在于可选节点列表中 */
export function isMiServiceMountSelectionValid(selected: unknown, mountOptions: ServiceMountNodeOption[]): boolean {
  const names = normalizeServiceMountGroupNames(selected);
  if (!names.length) return true;
  const allowed = new Set(mountOptions.map((item) => item.value));
  return names.every((name) => allowed.has(name));
}

/** 无表单上下文时按 value 解析入口节点展示（含 PD prefill/decode 前缀） */
export function formatMiServiceMountGroupNameDisplay(value: string, t: TFunction): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const decoded = decodeMiServiceMountOptionValue(raw);
  if (decoded.pdSegment === 'prefill') {
    return `${t('data-center:maas.service.entry.node.prefix.prefill')}: ${decoded.groupName}`;
  }
  if (decoded.pdSegment === 'decode') {
    return `${t('data-center:maas.service.entry.node.prefix.decode')}: ${decoded.groupName}`;
  }
  return raw;
}

/** 入口节点列表展示（确认步 / 详情；优先用当前表单选项 label） */
export function formatMiServiceMountGroupNamesDisplay(
  names: string[],
  t: TFunction,
  values?: ModelInstanceFormValues,
  fallback = '-',
): string {
  const normalized = normalizeServiceMountGroupNames(names);
  if (!normalized.length) return fallback;
  const labelByValue = values ? new Map(buildMiServiceMountNodeOptions(values, t).map((item) => [item.value, item.label])) : undefined;
  const display = normalized.map((value) => labelByValue?.get(value) ?? formatMiServiceMountGroupNameDisplay(value, t));
  return display.join('、');
}

/** 从 spec.network.service 汇总入口节点并格式化展示 */
export function formatMiServiceMountGroupNamesFromServices(
  services: { groupNames?: string[] }[] | null | undefined,
  t: TFunction,
  values?: ModelInstanceFormValues,
  fallback = '-',
): string {
  return formatMiServiceMountGroupNamesDisplay(resolveServiceMountGroupNamesFromServices(services), t, values, fallback);
}

/** 详情：从 spec.network.service 汇总入口节点并带拓扑前缀展示 */
export function formatMiServiceMountGroupNamesFromWorkload(
  services: { groupNames?: string[] }[] | null | undefined,
  spec: ModelInstanceSpec | undefined,
  t: TFunction,
  fallback = '-',
): string {
  const normalized = normalizeServiceMountGroupNames(resolveServiceMountGroupNamesFromServices(services));
  if (!normalized.length) return fallback;
  const labelByValue = new Map(buildMiServiceMountNodeOptionsFromSpec(spec, t).map((item) => [item.value, item.label]));
  return normalized.map((value) => labelByValue.get(value) ?? formatMiServiceMountGroupNameDisplay(value, t)).join('、');
}

/** 提交前：节点改名/删除后入口节点失效则拦截 */
export function validateMiServiceMountGroupNames(values: ModelInstanceFormValues, t: TFunction): MaasSubmitError | undefined {
  const mountOptions = buildMiServiceMountNodeOptions(values, t);
  const mountGroupNames = normalizeServiceMountGroupNames(values._serviceMountGroupName);
  if (!mountGroupNames.length) return undefined;
  if (isMiServiceMountSelectionValid(mountGroupNames, mountOptions)) return undefined;
  return {
    key: 'data-center:maas.service.entry.nodes.stale',
    fieldKey: 'data-center:maas.service.entry.nodes',
  };
}
